import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ServerLink } from '@/components/fleet/server-link';
import { ErrorState, TableSkeleton } from '@/components/fleet/states';
import { useProxyStats } from '@/hooks/use-proxy-stats';
import { countryFlag } from '@/lib/country-flag';

/**
 * Proxy-ingress overview — the browser-extension proxy tier. Lists every
 * active proxy-ingress server with its live session count (read from Redis
 * server-side). Read-only; server lifecycle is managed from the fleet list.
 */
export function ProxyIngressCard(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useProxyStats();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Proxy ingress</CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">
            {data.totalActive} active session{data.totalActive === 1 ? '' : 's'}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {isLoading && <TableSkeleton rows={3} />}

        {isError && (
          <ErrorState
            title="Couldn't load proxy-ingress stats."
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {data && data.servers.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No proxy-ingress servers. Add one from the fleet (role
            <span className="font-mono"> proxy_ingress</span>) to serve the
            browser extension.
          </p>
        )}

        {data && data.servers.length > 0 && (
          <ul className="divide-y" data-testid="proxy-ingress-list">
            {data.servers.map((server) => (
              <li
                key={server.name}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <ServerLink name={server.name} className="text-sm" />
                  <span className="text-xs text-muted-foreground">
                    {countryFlag(server.region)} {server.region ?? 'no region'} ·{' '}
                    {server.publicIp}
                  </span>
                </div>
                <span className="shrink-0 text-sm tabular-nums">
                  {server.activeSessions}
                  <span className="ml-1 text-xs text-muted-foreground">
                    session{server.activeSessions === 1 ? '' : 's'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
